import { useCallback, useEffect, useRef, useState } from 'react';
import type { Taxonomy, TaxonomyVersion } from '@/entities/taxonomy';
import { useDebouncedAction } from '@/shared/hooks';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { resolveHead, type DraftState } from './resolveHead';

const AUTOSAVE_DEBOUNCE_MS = 800;

/** What a save sends. Pinned by the tests, which assert on this exact shape. */
export interface SaveDraftPayload extends DraftState {
  expectedRevision: number;
}

export interface UseAutosaveDraftResult {
  draft: DraftState | null;
  /** Merges a patch and arms autosave. The only way a draft should be changed. */
  edit: (patch: Partial<DraftState>) => void;
  /** Edits made but not yet acknowledged by the server. Drives the header. */
  unsaved: boolean;
  /** Set when the server rejected a save because someone else got there first. */
  conflict: string | null;
  /** A save refused for a reason that is NOT a conflict — a 500, a dropped connection. */
  saveError: string | null;
  /**
   * Sends whatever is owed and resolves to whether the draft fully DRAINED.
   *
   * A caller gating on "everything typed is on the server" must branch on this
   * rather than on `conflict`: a 500 stops the loop without setting it, and even
   * a 409 only reaches the ref on the render after this resolves.
   */
  flushSave: () => Promise<boolean>;
  /** Clears the last failure and tries the same snapshot again. */
  retrySave: () => void;
  /** Abandons local edits so the seed effect re-runs against a refetch. */
  discardAndReseed: () => void;
}

/**
 * Debounced autosave for a taxonomy draft.
 *
 * Extracted from the builder unchanged. Everything below is load-bearing and
 * each piece is a bug that was already shipped once — read the individual notes
 * before altering any of it.
 *
 * Deliberately NOT shared with `TaxonomyListEditor`, which hand-rolls its own
 * autosave and looks like an obvious dedupe. That one is built on revision CAS
 * with 409 latching and an imperative `scheduleSave`, where this is a
 * declarative value-debounce; unifying them means reconciling two different
 * concurrency models.
 */
export function useAutosaveDraft(params: {
  taxonomy: Taxonomy | undefined;
  head: TaxonomyVersion | undefined;
  isDraft: boolean;
  saveDraftAsync: (payload: SaveDraftPayload) => Promise<Taxonomy>;
  /** Fallback message for a save failure the server did not explain. */
  saveFailedMessage: string;
  debounceMs?: number;
}): UseAutosaveDraftResult {
  const {
    taxonomy,
    head,
    isDraft,
    saveDraftAsync,
    saveFailedMessage,
    debounceMs = AUTOSAVE_DEBOUNCE_MS,
  } = params;

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [unsaved, setUnsaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const seededRef = useRef<string | null>(null);
  /**
   * The revision the current draft was seeded from, and the one our own saves
   * advance. Distinguishing "the server moved" from "we moved it" is the whole
   * job: `head.revision` alone cannot, because every autosave bumps it.
   */
  const seededRevisionRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);

  /**
   * `dirtyRef` drives the async save loop; `unsaved` drives the header. They
   * describe the same fact, so anything that moves one has to move the other —
   * clearing the ref alone left the header stuck on "Saving…" until the next
   * publish or keystroke happened to clear it.
   *
   * The one deliberate exception is inside the loop below, which clears the ref
   * before each request so a concurrent edit takes another pass. Calling
   * `markSaved` there would flash "All changes saved" mid-flight.
   */
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    setUnsaved(true);
    // A fresh edit supersedes the last failure, so the header must not keep
    // offering to retry a snapshot the user has already moved past.
    setSaveError(null);
  }, []);
  const markSaved = useCallback(() => {
    dirtyRef.current = false;
    setUnsaved(false);
    setSaveError(null);
  }, []);

  /**
   * Seeds the editable draft once per head version.
   *
   * `markSaved()` runs BEFORE `setDraft` on purpose: a freshly seeded draft must
   * never be observable as dirty, not even for one tick.
   *
   * `conflict` is cleared here too, and it is the whole failure this addresses.
   * A conflict says the server holds someone else's copy of the version that WAS
   * on screen; seeding a different one discards those stale local edits, which is
   * what resolving it means. Left set, it kept gating the save loop — so creating
   * or restoring a version while the banner was up produced a brand-new draft
   * with autosave silently dead and a banner about a version nobody was looking
   * at any more, recoverable only by pressing Reload latest.
   *
   * `markSaved` already resets the other two failure states, and that asymmetry
   * is what made this easy to miss: two of three reset on a reseed, and the third
   * did not.
   */
  useEffect(() => {
    if (!taxonomy || !head) return;
    if (seededRef.current === head.id) {
      // Unpublishing keeps the version id and only bumps its revision, so an
      // id-only gate left the grid showing the values this draft had pinned
      // until the page was reloaded. Re-seed when the server has moved AHEAD of
      // the last revision we accounted for.
      //
      // Ahead of, not different from: our own save records the revision it
      // produced before the query cache catches up, so `head` briefly reports
      // the older one — and an inequality test re-seeded off our own write,
      // wiping the edit that had just been saved.
      if (head.revision <= (seededRevisionRef.current ?? -1)) return;
      // A re-seed replaces the whole draft, so it must never run over edits
      // that have not reached the server. The save loop reconciles those — via
      // a 409 if someone else is the reason the revision moved.
      //
      // `savingRef` is the half that is easy to miss: the loop clears
      // `dirtyRef` BEFORE each request, so a head arriving mid-flight sees a
      // draft that looks clean while the snapshot it holds is still unsent.
      if (dirtyRef.current || savingRef.current) return;
    }
    seededRef.current = head.id;
    seededRevisionRef.current = head.revision;
    markSaved();
    setConflict(null);
    setDraft({
      name: taxonomy.name,
      description: head.description ?? '',
      market: head.market,
      platform: head.platform,
      fields: head.fields,
      rules: head.rules,
      settings: head.settings,
    });
  }, [taxonomy, head, markSaved]);

  /**
   * The revision to send, held in a REF rather than read from `head` in the
   * effect's dependencies.
   *
   * A successful save writes the server's response into the query cache, which
   * produces a new `head` object. Depending on it made the effect re-run on its
   * own result and save again — a loop that pushed one field rename to revision
   * 35 before this was caught.
   */
  const revisionRef = useRef(0);
  revisionRef.current = head?.revision ?? 0;

  // Read inside the async save loop rather than captured in its closure: the
  // loop outlives the render that started it, so a stale `isDraft`/`conflict`
  // would let it save into a version that has since been published.
  const draftRef = useRef<DraftState | null>(null);
  draftRef.current = draft;
  const isDraftRef = useRef(false);
  isDraftRef.current = isDraft;
  const conflictRef = useRef<string | null>(null);
  conflictRef.current = conflict;

  /**
   * Persists the current draft, with at most ONE save in flight.
   *
   * Saves must be serialized because the server increments `revision` on every
   * write and rejects a stale one. Firing a second save before the first
   * response landed sent the same revision twice: the server correctly refused
   * it, and the builder showed "Someone else edited this schema" to a user who
   * was editing alone — then stopped saving until they reloaded. Any autosave
   * slower than the debounce reproduced it.
   *
   * The loop re-reads `draftRef` after each round trip, so an edit made while a
   * save was in flight is picked up by the next pass instead of being lost.
   */
  const savingRef = useRef<Promise<boolean> | null>(null);
  const flushSave = useCallback(async (): Promise<boolean> => {
    // Already running: its loop will pick up whatever is dirty now.
    if (savingRef.current) return savingRef.current;

    const loop = (async () => {
      while (dirtyRef.current && isDraftRef.current && !conflictRef.current) {
        const snapshot = draftRef.current;
        if (!snapshot) break;
        // Cleared BEFORE the request, not after: an edit arriving while it is in
        // flight sets it again, and the loop takes another pass for that edit.
        dirtyRef.current = false;
        try {
          const updated = await saveDraftAsync({
            expectedRevision: revisionRef.current,
            name: snapshot.name,
            description: snapshot.description,
            market: snapshot.market,
            platform: snapshot.platform,
            fields: snapshot.fields,
            rules: snapshot.rules,
            settings: snapshot.settings,
          });
          // Taken from the RESPONSE, not from a re-render: the next iteration
          // may run before React has re-rendered with the updated cache, and
          // sending the previous revision would 409 against our own save.
          const savedHead = resolveHead(updated.versions, updated.currentVersionId);
          if (savedHead) {
            revisionRef.current = savedHead.revision;
            // Ours, so the seed effect must not read the resulting `head` as the
            // server moving underneath us and re-seed over what is on screen.
            seededRevisionRef.current = savedHead.revision;
          }
        } catch (error) {
          // The snapshot never landed, so it is still unsaved.
          markDirty();
          const { status, detail } = parseProblemDetail(error, saveFailedMessage);
          // Anything that is not a conflict is reported instead of latched.
          // `saveError` is deliberately NOT part of the loop condition: a
          // transient blip must not disable autosave for the session, so the
          // next edit re-arms the timer and tries again on its own.
          if (status === 409) setConflict(detail);
          else {
            // A non-conflict `detail` is validator prose: accurate, and no help
            // to the person who typed the value. They get the sentence; the
            // console keeps what is needed to diagnose it.
            // eslint-disable-next-line no-console -- the diagnosis the user is deliberately not shown
            console.error('Taxonomy draft save failed', { status, detail });
            setSaveError(saveFailedMessage);
          }
          break;
        }
      }
      const fullyDrained = !dirtyRef.current;
      if (fullyDrained) markSaved();
      return fullyDrained;
    })();

    savingRef.current = loop;
    try {
      const drained = await loop;
      return drained;
    } finally {
      savingRef.current = null;
    }
  }, [saveDraftAsync, saveFailedMessage, markDirty, markSaved]);

  // Trailing debounce over the draft itself. The dirty check keeps the seed from
  // saving a draft the user has not touched.
  useDebouncedAction(
    draft,
    () => {
      if (!dirtyRef.current) return;
      void flushSave();
    },
    debounceMs,
  );

  /**
   * Sends whatever is still pending when this screen goes away.
   *
   * The debounce clears its timer on cleanup, so without this the last edit
   * before a navigation is discarded rather than sent — and the cell editor
   * commits on blur, so clicking the breadcrumb commits an edit milliseconds
   * before unmounting. This route is a sibling of the tabs layout, not a child
   * of it, so leaving really does unmount. Covers client-side navigation only;
   * there is no `beforeunload` handler, so a tab close is not included.
   *
   * `flushSave` is read through a ref so its identity cannot re-run this and
   * flush mid-typing.
   */
  const flushRef = useRef(flushSave);
  flushRef.current = flushSave;
  useEffect(
    () => () => {
      if (dirtyRef.current) void flushRef.current();
    },
    [],
  );

  const edit = useCallback(
    (patch: Partial<DraftState>) => {
      // The mutation's own pending flag is false for the whole debounce window,
      // so deriving the header from it alone claimed "All changes saved" over an
      // edit that had not been sent yet.
      markDirty();
      setDraft((d) => (d ? { ...d, ...patch } : d));
    },
    [markDirty],
  );

  const retrySave = useCallback(() => {
    setSaveError(null);
    void flushSave();
  }, [flushSave]);

  const discardAndReseed = useCallback(() => {
    seededRef.current = null;
    markSaved();
    // Also cleared by the seed effect this arms, but synchronously here: leaving
    // it to the effect means one committed render still showing the banner the
    // user just dismissed.
    setConflict(null);
  }, [markSaved]);

  return {
    draft,
    edit,
    unsaved,
    conflict,
    saveError,
    flushSave,
    retrySave,
    discardAndReseed,
  };
}
